import { Fragment, type KeyboardEvent } from 'react'
import Box from '@mui/joy/Box'
import Chip from '@mui/joy/Chip'
import Sheet from '@mui/joy/Sheet'
import Table from '@mui/joy/Table'
import Tooltip from '@mui/joy/Tooltip'
import Typography from '@mui/joy/Typography'
import type { MobileOtaUpdate } from '@numeo/types-main-mobile'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { OtaUpdateDetail } from './OtaUpdateDetail'
import { HEALTH_CHIP, HEALTH_RULE, ago, percent, shortId } from './otaFormat'

const COLUMNS = 9

function Count({ value, alert }: { value: number; alert?: boolean }) {
    return (
        <Typography
            level="body-sm"
            textColor={value === 0 ? 'text.tertiary' : alert ? 'danger.600' : undefined}
            fontWeight={alert && value > 0 ? 'lg' : undefined}
        >
            {value}
        </Typography>
    )
}

function HealthChip({ update }: { update: MobileOtaUpdate }) {
    const { label, color } = HEALTH_CHIP[update.health]
    const { launches, rollbacks, downloads, failures } = update.last24h
    return (
        <Tooltip
            size="sm"
            variant="outlined"
            title={
                <Box sx={{ maxWidth: 280 }}>
                    <Typography level="body-xs">
                        Last 24h: {launches} launched, {rollbacks} rolled back, {downloads} downloaded, {failures} failed.
                    </Typography>
                    <Typography level="body-xs" textColor="text.tertiary" sx={{ mt: 0.5 }}>
                        {HEALTH_RULE}
                    </Typography>
                </Box>
            }
        >
            <Chip size="sm" variant="soft" color={color}>
                {label}
            </Chip>
        </Tooltip>
    )
}

function StatusCell({ update, live }: { update: MobileOtaUpdate; live: boolean }) {
    return (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {live ? (
                <Chip size="sm" variant="solid" color="success">
                    Live
                </Chip>
            ) : (
                <Chip size="sm" variant="soft" color={update.enabled ? 'primary' : 'neutral'}>
                    {update.enabled ? 'Enabled' : 'Disabled'}
                </Chip>
            )}
            {update.forceUpdate ? (
                <Chip size="sm" variant="soft" color="warning">
                    Force
                </Chip>
            ) : null}
            <Typography level="body-xs" textColor="text.tertiary">
                {update.rolloutPercent}%
            </Typography>
        </Box>
    )
}

interface Props {
    items: MobileOtaUpdate[]
    liveIds: Set<string>
    openId: string | null
    onToggle: (releaseId: string) => void
    now: number
}

export function OtaUpdatesTable({ items, liveIds, openId, onToggle, now }: Props) {
    const onKey = (event: KeyboardEvent, releaseId: string) => {
        if (event.key !== 'Enter' && event.key !== ' ') return
        event.preventDefault()
        onToggle(releaseId)
    }

    return (
        <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'auto' }}>
            <Table
                size="sm"
                hoverRow
                stickyHeader
                sx={{
                    '--TableCell-paddingY': '10px',
                    '& th': { whiteSpace: 'nowrap' },
                    '& td.num, & th.num': { textAlign: 'right' },
                    '& tr.ota-row:focus-visible': { outline: '2px solid', outlineColor: 'primary.500', outlineOffset: -2 },
                }}
            >
                <thead>
                    <tr>
                        <th style={{ width: 32 }} aria-label="Expand" />
                        <th style={{ width: '32%' }}>Update</th>
                        <th>Published</th>
                        <th>Status · rollout</th>
                        <th>Health (24h)</th>
                        <th className="num">Downloaded</th>
                        <th className="num">Launched</th>
                        <th className="num">On it now</th>
                        <th className="num">Rollbacks</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((update) => {
                        const open = openId === update.releaseId
                        const Chevron = open ? ChevronDown : ChevronRight
                        return (
                            <Fragment key={update.releaseId}>
                                <tr
                                    className="ota-row"
                                    tabIndex={0}
                                    aria-expanded={open}
                                    onClick={() => onToggle(update.releaseId)}
                                    onKeyDown={(event) => onKey(event, update.releaseId)}
                                    style={{ cursor: 'pointer', opacity: update.enabled ? 1 : 0.75 }}
                                >
                                    <td>
                                        <Chevron size={16} />
                                    </td>
                                    <td>
                                        <Typography
                                            level="title-sm"
                                            sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                                        >
                                            {update.message ?? 'No message'}
                                        </Typography>
                                        <Typography level="body-xs" textColor="text.tertiary">
                                            v{update.appVersion ?? '—'} · {shortId(update.bundleId)}
                                        </Typography>
                                        {update.disableReason ? (
                                            <Typography level="body-xs" color="warning" sx={{ mt: 0.25 }}>
                                                Disabled: {update.disableReason}
                                            </Typography>
                                        ) : null}
                                    </td>
                                    <td>
                                        <Tooltip size="sm" title={new Date(update.createdAt).toLocaleString()}>
                                            <Typography level="body-sm" sx={{ whiteSpace: 'nowrap' }}>
                                                {ago(update.createdAt, now)}
                                            </Typography>
                                        </Tooltip>
                                    </td>
                                    <td>
                                        <StatusCell update={update} live={liveIds.has(update.releaseId)} />
                                    </td>
                                    <td>
                                        <HealthChip update={update} />
                                        {update.last24h.failures > 0 ? (
                                            <Typography level="body-xs" color="danger" sx={{ mt: 0.25 }}>
                                                {update.last24h.failures} failed
                                            </Typography>
                                        ) : null}
                                    </td>
                                    <td className="num">
                                        <Count value={update.downloaded} />
                                    </td>
                                    <td className="num">
                                        <Count value={update.launched} />
                                    </td>
                                    <td className="num">
                                        <Typography level="body-sm" textColor={update.activeShare ? undefined : 'text.tertiary'}>
                                            {percent(update.activeShare)}
                                        </Typography>
                                    </td>
                                    <td className="num">
                                        <Count value={update.rolledBack} alert />
                                    </td>
                                </tr>
                                {open ? (
                                    <tr>
                                        <td colSpan={COLUMNS} style={{ background: 'var(--joy-palette-background-level1)' }}>
                                            <OtaUpdateDetail releaseId={update.releaseId} />
                                        </td>
                                    </tr>
                                ) : null}
                            </Fragment>
                        )
                    })}
                </tbody>
            </Table>
        </Sheet>
    )
}
